import { Check, X, Info, Loader2, BookOpen, AlertTriangle } from "lucide-react";
import { ResponsiveTooltip } from "@/components/ui/responsive-tooltip";
import { cn } from "@/lib/utils";
import type { ScanStatus } from "@/hooks/useScanProgress";

interface PlatformCardProps {
  name: string;
  found: boolean;
  context?: string;
  confidence?: 'high' | 'medium' | 'low';
  status?: ScanStatus;
  skipAnimation?: boolean;
  infoTooltip?: string;
  spellingCorrected?: boolean;
  suggestedQuery?: string;
  onLearnMore?: () => void;
}

const PlatformCard = ({ name, found, context, confidence, status = "success", skipAnimation = false, infoTooltip, spellingCorrected, suggestedQuery, onLearnMore }: PlatformCardProps) => {
  const confidenceColors = {
    high: 'text-success',
    medium: 'text-warning',
    low: 'text-muted-foreground',
  };

  const isLoading = status === "loading";
  const isError = status === "error";

  return (
    <div className={cn(
      "flex flex-col p-4 rounded-xl bg-secondary/50 border border-border",
      "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md",
      !isLoading && !isError && found && "border-success/30 bg-success/5",
      !isLoading && !isError && !found && "border-error/30 bg-error/5",
      isLoading && "border-primary/30 bg-primary/5"
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{name}</span>
          {infoTooltip && (
            <ResponsiveTooltip content={<p className="text-sm max-w-xs">{infoTooltip}</p>} side="top">
              <button type="button" className="inline-flex items-center justify-center cursor-pointer">
                <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
              </button>
            </ResponsiveTooltip>
          )}
          {onLearnMore && (
            <ResponsiveTooltip content={<p className="text-sm">Learn more about this platform</p>} side="top">
              <button
                type="button"
                onClick={onLearnMore}
                className="relative inline-flex items-center justify-center cursor-pointer group"
                aria-label="Learn more"
              >
                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping-once" />
                <span className="relative p-1 rounded-full group-hover:bg-primary/10 transition-colors">
                  <BookOpen className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors animate-methodology-pulse" />
                </span>
              </button>
            </ResponsiveTooltip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <span className="text-sm text-primary font-medium">Scanning...</span>
            </>
          ) : isError ? (
            <>
              <X className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground font-medium">Unavailable</span>
            </>
          ) : found ? (
            <>
              <Check className={cn("w-5 h-5 text-success", !skipAnimation && "animate-bounce-in")} />
              <span className="text-sm text-success font-medium">Found</span>
            </>
          ) : (
            <>
              <X className="w-5 h-5 text-error" />
              <span className="text-sm text-error font-medium">Not found</span>
            </>
          )}
        </div>
      </div>
      
      {/* Spelling correction warning */}
      {spellingCorrected && suggestedQuery && !isLoading && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <p className="text-xs text-warning flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Google autocorrected to "<strong>{suggestedQuery}</strong>". Your brand name may need more online presence to be recognized.
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default PlatformCard;
